import { CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row } from 'reactstrap';
import Toast from '../../helpers/Toast';
import { AssistanceDTO } from '../../models/Assistance/AssistanceDTO';
import { AssistanceProfitabilityDTO } from '../../models/Profitability/AssistanceProfitabilityDTO';
import { AssistanceProfitabilityParametersDTO } from '../../models/Profitability/AssistanceProfitabilityParametersDTO';
import { ProfitabilityPartsAndHoursDTO } from '../../models/Profitability/ProfitabilityPartsAndHoursDTO';
import { AssistanceService } from '../../services/AssistanceService';
import TableComponent from '../TableComponent';

export type AssistanceProfitabilityProps = {
    assistanceId: number;
    reloadExecutions: boolean;
    mode: "workshop" | "backoffice";
}

function AssistanceProfitabilityTable({ assistanceId, reloadExecutions, mode }: AssistanceProfitabilityProps) {

    const assistanceService = new AssistanceService();
    const pageSizes: number[] = [50, 100];
    const [data, setData] = useState<ProfitabilityPartsAndHoursDTO[]>([]);
    const [assistanceProfitability, setAssistanceProfitability] = useState<AssistanceProfitabilityDTO>();
    const [pageSize, setPageSize] = useState<number>(50);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [goToPage, setGoToPage] = useState<number>();
    const [pageCount, setPageCount] = useState(1);
    const [reload, setReload] = useState<boolean>(false);
    const intl = useIntl();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingCount, setLoadingCount] = useState<number>(0);

    // orçamento
    const [isLoadingAssistance, setIsLoadingAssistance] = useState<boolean>(false);
    const [assistance, setAssistance] = useState<AssistanceDTO>({} as AssistanceDTO);


    const columns = React.useMemo(
        () => [
            {
                Header: "Referência",
                accessor: "partErpId",
                width: 6.25,
            },
            {
                Header: "Descrição",
                accessor: "partDescription",
                width: 10.25,
            },
            {
                Header: "Quantidade",
                accessor: "partQuantity",
                width: 2.25,
            },
            {
                Header: "Preço de Venda",
                accessor: "partSellingPrice",
                width: 6.25,
                Cell: (props: any) => {
                    const { partSellingPrice } = props.cell.row.original;
                    const formattedPartSellingPrice = partSellingPrice ? intl.formatNumber(partSellingPrice, { style: 'currency', currency: 'EUR' }) : '-';
                    return <div className="table_align-right">{formattedPartSellingPrice}</div>;
                },
            },
            {
                Header: "Preço de Custo",
                accessor: "partCostPrice",
                width: 6.25,
                Cell: (props: any) => {
                    const { partCostPrice } = props.cell.row.original;
                    const formattedPartCostPrice = partCostPrice ? intl.formatNumber(partCostPrice, { style: 'currency', currency: 'EUR' }) : '-';
                    return <div className="table_align-right">{formattedPartCostPrice}</div>;
                },
            },
            {
                Header: "Lucro (€)",
                accessor: "partProfit",
                width: 6.25,
                Cell: (props: any) => {
                    const { partProfit } = props.cell.row.original;
                    const formattedPartProfit = partProfit ? intl.formatNumber(partProfit, { style: 'currency', currency: 'EUR' }) : '-';
                    return <div className="table_align-right">{formattedPartProfit}</div>;
                },
            },
            {
                Header: "Lucro Total Linha(€)",
                accessor: "totalLineProfit",
                width: 6.25,
                Cell: (props: any) => {
                    const { totalLineProfit } = props.cell.row.original;
                    const formattedTotalLineProfit = totalLineProfit ? intl.formatNumber(totalLineProfit, { style: 'currency', currency: 'EUR' }) : '-';
                    return <div className="table_align-right">{formattedTotalLineProfit}</div>;
                },
            },
        ],
        [],
    );

    const fetchData = async () => {
        setLoading(true);

        //obter assistencia
        setIsLoadingAssistance(true);

        var assistanceResponse = await assistanceService.GetById(Number(assistanceId));
        if (assistanceResponse.success === false || assistance == null) {
            setIsLoadingAssistance(false);
            Toast.Show("error", assistanceResponse.message);
            return;
        }
        setIsLoadingAssistance(false);

        const profitabilityParams: AssistanceProfitabilityParametersDTO = {
            docType: assistanceResponse.obj?.erpDocType ?? '',
            serie: assistanceResponse.obj?.erpSerie?.toString() ?? '',
            number: assistanceResponse.obj?.erpNumDoc ?? 0,
            assistanceBudget: assistanceResponse.obj?.budgetSellingPrice ?? 0,
            showBillabledDocuments: false
        };

        const response = await assistanceService.GetAssistanceProfitability(assistanceId, profitabilityParams);

        if (response.success === false && response.obj === null) {
            setData([]);
            Toast.Show("error", response.message);
            setLoading(false);
            setLoadingCount(1);
            return;
        }

        setData(response.obj.partsAndHours);
        setAssistanceProfitability(response.obj);

        setLoading(false);
        setLoadingCount(1);
    };

    useEffect(() => {
        if (reloadExecutions) {
            fetchData();
            setReload(false);
        }
    }, [reloadExecutions]);

    return (
        <div>
            <Row style={{ margin: "1rem 0px" }}>
                <h5><FormattedMessage id={"Screens.Assistances.Profitability.Title"}></FormattedMessage></h5>
                <br></br>
                <br></br>

                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <div>
                        <p>
                            <strong>
                                <FormattedMessage id={"Screens.Assistances.Profitability.Predicted"} />
                            </strong>{" "}
                        </p>
                        <p>
                            <FormattedMessage id={"Screens.Assistances.Profitability.BudgetSellingPrice"} />
                            {intl.formatNumber(assistanceProfitability?.budgetSellingPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p>
                            <FormattedMessage id={"Screens.Assistances.Profitability.BudgetCostPrice"} />
                            {intl.formatNumber(assistanceProfitability?.budgetCostPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p>
                            <FormattedMessage id={"Screens.Assistances.Profitability.PredictedProfit"} />
                            {intl.formatNumber(assistanceProfitability?.predictedProfit ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p>
                            <FormattedMessage id={"Screens.Assistances.Profitability.PredictedSellingMargin"} />
                            {intl.formatNumber((assistanceProfitability?.predictedSellingMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>

                        <p>
                            <FormattedMessage id={"Screens.Assistances.Profitability.PredictedCostMargin"} />
                            {intl.formatNumber((assistanceProfitability?.predictedCostMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{ marginRight: '1.5rem' }}>
                            <strong>
                                <FormattedMessage id={"Screens.Assistances.Profitability.Real"} />
                            </strong>{" "}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Assistances.Profitability.TotalSellingPrice"} />
                            {intl.formatNumber(assistanceProfitability?.totalSellingPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Assistances.Profitability.TotalCostPrice"} />
                            {intl.formatNumber(assistanceProfitability?.totalCostPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Assistances.Profitability.TotalProfit"} />
                            {intl.formatNumber(assistanceProfitability?.totalProfit ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Assistances.Profitability.RealSellingMargin"} />
                            {intl.formatNumber((assistanceProfitability?.realSellingMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Assistances.Profitability.RealCostMargin"} />
                            {intl.formatNumber((assistanceProfitability?.realCostMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>
                    </div>
                </div>
                <br></br>
                <TableComponent
                    columns={columns}
                    data={data}
                    goToPage={goToPage}
                    initialPage={currentPage}
                    pageCount={pageCount}
                    initialPageSize={pageSize}
                    pageSizes={pageSizes}
                    manualPagination={true}
                    loading={isLoading}
                    fetchData={fetchData}
                    initialSortBy={[]}
                    disableSortBy={true}
                />
            </Row>
            {loading === true && loadingCount !== 1 && (
                <div className="loading">
                    <span>
                        {" "}
                        <FormattedMessage id="Common.Loading.DataLoading" />
                        ...{" "}
                    </span>
                    <CircularProgress style={{ marginLeft: "10px" }} size={"25px"} />
                </div>
            )}
        </div>
    )
}

export default AssistanceProfitabilityTable